import { useEffect, useRef, useState } from 'react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useEnsureOrganisationMutation } from 'src/shared/state/api-slices/organisationEngineSlice';
import type { LegacyCustomer } from './findEstateDiscrepancies';
import { type BulkOutcome, type BulkRun, newRun, withOutcome } from './bulkRun';

/** The page has gone: its queries are unsubscribed, so a refetch would throw. */
const PAGE_LEFT = 'The user left Estate Management during a bulk onboarding run.';

const failureReason = (error: unknown): string => {
    const { status, data } = error as FetchBaseQueryError & { data?: { error?: string } };
    if (typeof status === 'number') {
        return [status, data?.error].filter(Boolean).join(' ');
    } else if (status === 'FETCH_ERROR') {
        return 'Network error';
    } else {
        return String(status);
    }
};

/** The run lives with the page, not with a tab: a run stops when the page goes, not when the tab changes. */
export const useBulkOnboarding = (onRunEnded: () => void) => {
    const [run, setRun] = useState<BulkRun | null>(null);
    const [onboard] = useEnsureOrganisationMutation();
    const runController = useRef<AbortController | null>(null);

    useEffect(() => () => runController.current?.abort(PAGE_LEFT), []);

    const onboardOne = async (customer: LegacyCustomer): Promise<BulkOutcome> => {
        try {
            return { kind: await onboard({ business_central_id: customer.customerId, name: customer.customerName }).unwrap() };
        } catch (error) {
            return { kind: 'failed', reason: failureReason(error) };
        }
    };

    const start = async (customers: LegacyCustomer[]) => {
        const controller = new AbortController();
        runController.current = controller;
        setRun(newRun(customers));
        for (const customer of customers) {
            if (controller.signal.aborted === false) {
                const outcome = await onboardOne(customer);
                setRun(current => current && withOutcome(current, customer.customerId, outcome));
            }
        }
        const isPageOpen = controller.signal.reason !== PAGE_LEFT;
        if (isPageOpen) {
            const status = controller.signal.aborted ? 'stopped' : 'finished';
            setRun(current => current && { ...current, status });
            onRunEnded();
        }
    };

    return {
        run,
        start,
        stop: () => runController.current?.abort(),
        clear: () => setRun(null),
    };
};
